<!-- Shared row content: root rows get the icon, all rows get the truncating (search-bolded) label. -->
<ng-template #itemContent>
    @if (isRoot() && sidebarItem().iconImageSrc) {
        <img [src]="sidebarItem().iconImageSrc" [alt]="sidebarItem().label" class="h-[17px] w-[17px] shrink-0 rounded-[3px] object-contain" />
    } @else if (isRoot() && sidebarItem().icon) {
        <span class="material-symbols-outlined sidebar-item-icon shrink-0 text-[1.25rem]/tight!">{{ sidebarItem().icon }}</span>
    }
    <span class="min-w-0 grow truncate" [attr.title]="sidebarItem().label">
        @for (segment of labelSegments(); track $index) {
            <span [class.font-semibold]="segment.isMatch">{{ segment.text }}</span>
        }
    </span>
</ng-template>

@if (isRoot() && sidebarItem().items?.length) {
    <!-- Root domain: collapsible top-level section -->
    <button
        type="button"
        class="sidebar-row text-color flex h-[32px] w-full cursor-pointer items-center gap-[8px] rounded-[4px] pr-[9px] pl-[9px] text-left text-[0.95rem]/tight font-medium transition-colors duration-200 hover:bg-(--p-navigation-item-focus-background)"
        (click)="onClick()"
    >
        <ng-container [ngTemplateOutlet]="itemContent" />
        @if (sidebarItem().trailingBadge) {
            <span class="shrink-0 rounded-[3px] border border-(--p-content-border-color) px-1 py-px text-[9px]/tight font-semibold tracking-[0.06em] text-(--p-surface-400) uppercase dark:text-(--p-surface-500)">
                {{ sidebarItem().trailingBadge }}
            </span>
        }
        <span [class.rotate-180]="isChildrenExpanded()" class="material-symbols-outlined shrink-0 text-[16px]/tight! text-(--p-surface-400) transition-transform duration-200 dark:text-(--p-surface-500)">keyboard_arrow_down</span>
    </button>
} @else if (sidebarItem().items?.length) {
    <!-- Group caption (REPORTS / SETUP / ...): non-interactive micro-heading -->
    <div aria-hidden="true" class="flex h-[22px] items-center pr-[9px] pl-[33px] text-[0.6875rem]/tight font-semibold tracking-[0.06em] text-(--p-surface-400) uppercase select-none dark:text-(--p-surface-500)">
        {{ sidebarItem().label }}
    </div>
} @else if (!sidebarItem().routerLink) {
    <!-- External link: legacy bridge or non-router navigation -->
    <a
        class="sidebar-row text-color flex cursor-pointer items-center gap-[8px] rounded-[4px] pr-[9px] text-[0.95rem]/tight transition-colors duration-200 hover:bg-(--p-navigation-item-focus-background)"
        [ngClass]="{
            'sidebar-highlighted': isHighlighted(),
            'h-[32px] pl-[9px] font-medium': isRoot(),
            'h-[28px] pl-[33px] font-normal': !isRoot()
        }"
        [attr.href]="sidebarService.resolveExternalUrl(sidebarItem())"
        [attr.data-active-search-result]="isActiveSearchResult() ? 'true' : null"
        (click)="onClick()"
    >
        <ng-container [ngTemplateOutlet]="itemContent" />
    </a>
} @else {
    <!-- Routed page: standard in-app navigation item -->
    <a
        class="sidebar-row text-color flex cursor-pointer items-center gap-[8px] rounded-[4px] pr-[9px] text-[0.95rem]/tight transition-colors duration-200 hover:bg-(--p-navigation-item-focus-background)"
        [ngClass]="{
            'sidebar-highlighted': isHighlighted(),
            'sidebar-page-active': isRouteActive(),
            'h-[32px] pl-[9px] font-medium': isRoot(),
            'h-[28px] pl-[33px] font-normal': !isRoot()
        }"
        [routerLink]="sidebarItem().routerLink"
        [queryParams]="routerLinkQueryParams()"
        [attr.data-active-search-result]="isActiveSearchResult() ? 'true' : null"
        (click)="onClick()"
    >
        <ng-container [ngTemplateOutlet]="itemContent" />
        @if (isPinnable()) {
            <button
                type="button"
                class="sidebar-pin-button flex h-[20px] w-[20px] shrink-0 cursor-pointer items-center justify-center rounded-[3px] border-0 bg-transparent p-0 text-(--p-surface-400) hover:text-(--p-text-color) disabled:cursor-default disabled:opacity-40 dark:text-(--p-surface-500)"
                [disabled]="isPinDisabled()"
                [attr.title]="pinTitle()"
                (click)="onTogglePinned($event)"
            >
                <span class="material-symbols-outlined text-[15px]/none!">{{ isPinned() ? 'keep_off' : 'keep' }}</span>
            </button>
        }
    </a>
}

@if (sidebarItem().items?.length) {
    <!-- Keep child items in the DOM and collapse them with CSS so search can reveal matches cleanly. -->
    <div [class.sidebar-children-collapsed]="!isChildrenExpanded()" class="sidebar-children">
        <ul class="flex flex-col gap-[2px]">
            @for (item of sidebarItem().items; track $index) {
                <li>
                    <app-sidebar-item [sidebarItem]="item"></app-sidebar-item>
                </li>
            }
        </ul>
    </div>
}
